import { useState, type KeyboardEvent } from 'react';

export interface UseChartHoverResult {
  /** The currently hovered-or-focused data index, or `null` when nothing is active. */
  activeIndex: number | null;
  /** Set directly from a datum's `onPointerEnter`/`onPointerMove` — see `BarChart`/`LineChart`. */
  setActiveIndex: (index: number | null) => void;
  /**
   * Spread onto the chart's root hit area (the `<svg>` itself): makes it keyboard-focusable,
   * clears the active index on pointer-leave, and moves it one step per arrow-key press so a
   * keyboard user gets the same tooltip a mouse user gets from hover.
   */
  rootProps: {
    tabIndex: number;
    onPointerLeave: () => void;
    onKeyDown: (event: KeyboardEvent<Element>) => void;
  };
}

/**
 * Shared "what's the active data point" state for `BarChart` and `LineChart`. Deliberately does
 * NOT try to compute an index from a raw pointer position — the two charts lay data out on
 * different axes (rows vs. x-positions), so each one draws its own per-index transparent hit
 * band (a `<rect>` per bar/point) and calls `setActiveIndex` from that band's own
 * `onPointerEnter`/`onPointerMove`. This hook owns only the resulting state plus the
 * keyboard/pointer-leave wiring that is identical either way.
 */
export function useChartHover(count: number): UseChartHoverResult {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  function onKeyDown(event: KeyboardEvent<Element>) {
    if (count === 0) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => (current === null ? 0 : Math.min(count - 1, current + 1)));
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => (current === null ? count - 1 : Math.max(0, current - 1)));
    } else if (event.key === 'Escape') {
      setActiveIndex(null);
    }
  }

  return {
    activeIndex,
    setActiveIndex,
    rootProps: {
      tabIndex: 0,
      onPointerLeave: () => setActiveIndex(null),
      onKeyDown,
    },
  };
}
